import React from 'react';
import styled, { keyframes } from 'styled-components';

const AIWebinarTestimonialES = () => {
  return (
    <TestimonialContainer>
      <OverlayGradient />
      <ContentWrapper>
        <HeaderSection>
          <TestimonialBadge>CASO DE ÉXITO</TestimonialBadge>
          <Title>Mi Viaje: De la Incertidumbre a la Eficiencia con IA</Title>
          <Subtitle>Una historia real sobre cómo la IA transformó nuestro proceso de contratación y desarrollo</Subtitle>
        </HeaderSection>

        <StorySection>
          <StoryPart>
            <StoryTitle>El Desafío</StoryTitle>
            <StoryText>
              Como director de tecnología de una startup en crecimiento, me enfrentaba a un dilema común: necesitábamos expandir nuestro equipo de ingeniería, pero los costos de contratación y desarrollo eran astronómicos. Los ingenieros senior cobraban más de $150,000 al año, y los costos de reclutamiento y capacitación añadían otros $30,000 por persona. Lo que es aún más preocupante es que mientras luchábamos con estos costos, noté que empresas visionarias ya habían estado implementando soluciones de IA durante más de dos años. La brecha tecnológica entre los primeros adoptantes y las empresas tradicionales se estaba volviendo cada vez más evidente.
            </StoryText>
          </StoryPart>

          <StoryPart>
            <StoryTitle>El Descubrimiento</StoryTitle>
            <StoryText>
              Durante el webinar de IA, aprendí sobre herramientas de automatización que podrían reducir significativamente nuestra dependencia de contrataciones costosas. La idea de usar IA para optimizar nuestro flujo de trabajo y automatizar tareas repetitivas me pareció revolucionaria. Lo que más me impactó fue darme cuenta de cuán atrasadas están muchas pequeñas empresas en la adopción de estas tecnologías. En países como Japón y Corea del Sur, la adopción de IA en pequeñas empresas ya alcanza el 40%, mientras que en muchos países occidentales apenas rozamos el 15%. Esta brecha tecnológica podría tener serias implicaciones para la competitividad global en los próximos años.
            </StoryText>
          </StoryPart>

          <StoryPart>
            <StoryTitle>La Implementación</StoryTitle>
            <StoryText>
              Implementamos un sistema de IA que automatizó el 40% de nuestras tareas de desarrollo. Esto nos permitió:
            </StoryText>
            <BenefitList>
              <BenefitItem>
                <BenefitIcon>⚡</BenefitIcon>
                <BenefitText>Reducir el tiempo de desarrollo en un 60%</BenefitText>
              </BenefitItem>
              <BenefitItem>
                <BenefitIcon>💰</BenefitIcon>
                <BenefitText>Ahorrar $200,000 en costos de contratación</BenefitText>
              </BenefitItem>
              <BenefitItem>
                <BenefitIcon>🚀</BenefitIcon>
                <BenefitText>Acelerar el lanzamiento de productos en un 75%</BenefitText>
              </BenefitItem>
            </BenefitList>
          </StoryPart>

          <StoryPart>
            <StoryTitle>Los Resultados</StoryTitle>
            <StoryText>
              En seis meses, nuestra productividad se disparó. Pudimos mantener un equipo más pequeño pero más eficiente, y los ahorros nos permitieron invertir en otras áreas críticas del negocio. La IA no reemplazó a nuestros ingenieros; en cambio, los hizo más efectivos y les permitió enfocarse en tareas más estratégicas. Lo que es particularmente preocupante es que muchas empresas en países en desarrollo siguen siendo reacias a adoptar estas tecnologías, lo que podría llevar a una brecha tecnológica significativa en el mercado global. Los países que no abrazan las soluciones de IA arriesgan quedarse atrás en la economía global, ya que la brecha entre empresas impulsadas por IA y las tradicionales sigue ampliándose.
            </StoryText>
          </StoryPart>

          <StoryPart>
            <StoryTitle>El Impacto Global</StoryTitle>
            <StoryText>
              Las implicaciones de la adopción de IA se extienden mucho más allá de las empresas individuales. Estamos viendo una clara división entre los países que están promoviendo activamente la integración de IA y aquellos que se están quedando atrás. En regiones como el Sudeste Asiático y partes de África, la falta de infraestructura y experiencia en IA podría llevar a desafíos económicos significativos en la próxima década. La misión de BBGlobal de democratizar la tecnología de IA se vuelve cada vez más crucial en este contexto, ya que cerrar esta brecha tecnológica es esencial para la estabilidad y el crecimiento económico global.
            </StoryText>
          </StoryPart>

          <CallToAction>
            <CTATitle>¿Listo para Transformar tu Equipo?</CTATitle>
            <CTAText>
              Únete a nuestro próximo webinar y descubre cómo puedes implementar estrategias similares en tu negocio.
            </CTAText>
            <CTAButton 
              href="https://calendly.com/pmteamwinners/ai-for-better-business-webinar" 
              target="_blank"
              rel="noopener noreferrer"
            >
              RESERVA TU LUGAR
              <ArrowIcon>→</ArrowIcon>
            </CTAButton>
          </CallToAction>
        </StorySection>

        <AuthorSection>
          <AuthorInfo>
            <AuthorName>Carlos Rodríguez</AuthorName>
            <AuthorTitle>Director de Tecnología, TechStartup</AuthorTitle>
            <AuthorStats>
              <Stat>
                <StatValue>$200K+</StatValue>
                <StatLabel>Ahorro en Costos</StatLabel>
              </Stat>
              <Stat>
                <StatValue>60%</StatValue>
                <StatLabel>Mejora en Eficiencia</StatLabel>
              </Stat>
              <Stat>
                <StatValue>75%</StatValue>
                <StatLabel>Más Rápido al Mercado</StatLabel>
              </Stat>
            </AuthorStats>
          </AuthorInfo>
        </AuthorSection>
      </ContentWrapper>

      <FloatingElements>
        <FloatingCircle top="10%" left="5%" size="100px" opacity="0.03" />
        <FloatingCircle top="70%" left="15%" size="150px" opacity="0.02" />
        <FloatingCircle top="30%" right="10%" size="120px" opacity="0.03" />
        <GridLines />
      </FloatingElements>
    </TestimonialContainer>
  );
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 255, 198, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 15px rgba(0, 255, 198, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 255, 198, 0);
  }
`;

const float = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
`;

// Styled Components
const TestimonialContainer = styled.div`
  min-height: 100vh;
  background-color: #0a0e17;
  position: relative;
  overflow: hidden;
  padding: 100px 80px;
  
  @media (max-width: 991px) {
    padding: 80px 40px;
  }
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const OverlayGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(10, 14, 23, 0.8) 0%, rgba(15, 20, 35, 0.9) 100%);
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1000px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const TestimonialBadge = styled.div`
  display: inline-block;
  background: rgba(0, 255, 198, 0.1);
  color: #00FFC6;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 0.9rem;
  margin-bottom: 20px;
  border: 1px solid rgba(0, 255, 198, 0.2);
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #a0a0a0;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
`;

const StorySection = styled.div`
  margin-bottom: 60px;
`;

const StoryPart = styled.div`
  margin-bottom: 40px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const StoryTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #00FFC6;
  margin-bottom: 20px;
`;

const StoryText = styled.p`
  font-size: 1.1rem;
  color: #e0e0e0;
  line-height: 1.8;
  margin-bottom: 20px;
`;

const BenefitList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(0, 255, 198, 0.1);
`;

const BenefitIcon = styled.span`
  font-size: 1.5rem;
  margin-right: 15px;
`;

const BenefitText = styled.span`
  color: #e0e0e0;
  font-size: 1rem;
`;

const CallToAction = styled.div`
  text-align: center;
  background: linear-gradient(135deg, rgba(15, 20, 35, 0.95), rgba(10, 14, 23, 0.95));
  border-radius: 16px;
  padding: 40px;
  margin-top: 60px;
  border: 1px solid rgba(0, 255, 198, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 15px;
`;

const CTAText = styled.p`
  font-size: 1.1rem;
  color: #a0a0a0;
  margin-bottom: 30px;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00FFC6, #0057FF);
  color: #000;
  font-weight: 700;
  font-size: 1rem;
  padding: 18px 35px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 25px rgba(0, 255, 198, 0.3);
  animation: ${pulse} 2s infinite;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 255, 198, 0.4);
  }
`;

const ArrowIcon = styled.span`
  margin-left: 8px;
  font-size: 1.2rem;
  transition: transform 0.3s ease;
  
  ${CTAButton}:hover & {
    transform: translateX(5px);
  }
`;

const AuthorSection = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const AuthorInfo = styled.div`
  text-align: center;
`;

const AuthorName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 5px;
`;

const AuthorTitle = styled.p`
  font-size: 1rem;
  color: #a0a0a0;
  margin-bottom: 20px;
`;

const AuthorStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Stat = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #00FFC6;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #a0a0a0;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const FloatingCircle = styled.div`
  position: absolute;
  width: ${props => props.size};
  height: ${props => props.size};
  border-radius: 50%;
  background: rgba(0, 255, 198, ${props => props.opacity});
  top: ${props => props.top};
  left: ${props => props.left};
  animation: ${float} 6s ease-in-out infinite;
`;

const GridLines = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(0, 255, 198, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 255, 198, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  opacity: 0.3;
`;

export default AIWebinarTestimonialES; 